import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { generalMessages } from 'src/registry/utils/messages';
import { commonMessages } from 'src/utils/messages';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';
import { BILLING_RUN_TYPE } from 'src/features/billingRuns/utils/types';
import { BILLING_RUN_TYPE_MAP } from 'src/features/billingRuns/utils/constants';
import {
    triggerBillingRunFormSchema,
    TriggerBillingRunFormSchemaValues,
} from 'src/features/billingRuns/utils/formSchema';
import { useTriggerBillingRun } from 'src/features/billingRuns/api/useTriggerBillingRun';
import { LIST_BILLING_RUNS_RESOURCE } from 'src/features/billingRuns/api/useListBillingRuns';
import { billingRunsMessages } from 'src/features/billingRuns/utils/messages';

function TriggerBillingRunFormModal<TData>({ className }: DataTableNewEntryProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const onError = useOnError();

    const form = useForm<TriggerBillingRunFormSchemaValues>({
        resolver: zodResolver(triggerBillingRunFormSchema),
        defaultValues: { type: BILLING_RUN_TYPE.CHARGING, business_date: undefined },
    });

    const { mutate, isPending } = useTriggerBillingRun({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLING_RUNS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(billingRunsMessages.triggerBillingRunSuccess),
                description: formatMessage(billingRunsMessages.triggerBillingRunSuccessDescription),
            });
        },
    });

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: TriggerBillingRunFormSchemaValues) => {
            mutate(values, { onError: (error) => onError(error, form.setError) });
        },
        [mutate, onError, form],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button className={cn('ml-auto', className)}>
                    <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} />
                    {formatMessage(billingRunsMessages.triggerBillingRun)}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.WAVE_PULSE} />
                        <div>
                            <DialogTitle>{formatMessage(billingRunsMessages.triggerBillingRun)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(billingRunsMessages.triggerBillingRunSubtitle)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
                        <SelectFormField<TriggerBillingRunFormSchemaValues>
                            form={form}
                            name="type"
                            label={formatMessage(commonMessages.type)}
                            options={Object.values(BILLING_RUN_TYPE)}
                            optionTranslationMap={BILLING_RUN_TYPE_MAP}
                        />
                        <FormField
                            control={form.control}
                            name="business_date"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog">
                                        {formatMessage(billingRunsMessages.businessDate)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            <CalendarWithTime
                                                dateISO={field.value ?? undefined}
                                                onChange={field.onChange}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <FormDescription>{formatMessage(billingRunsMessages.businessDateHint)}</FormDescription>
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(billingRunsMessages.triggerBillingRun)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TriggerBillingRunFormModal;
